import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Code,
  CopyButton,
  Divider,
  Grid,
  Group,
  NumberInput,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
  Modal,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  IconArrowLeft,
  IconAlertCircle,
  IconCheck,
  IconCopy,
  IconPlus,
} from '@tabler/icons-react';
import { merchantsApi, type CreateMerchantInput } from '../api/merchants';
import type { MerchantCredentialsReveal } from '../api/types';
import { useProfile } from '../auth/SessionContext';
import { useTranslation } from '../i18n/LocaleContext';

export function MerchantCreatePage() {
  const { t } = useTranslation();
  const profile = useProfile();
  const navigate = useNavigate();
  const [revealed, setRevealed] = useState<MerchantCredentialsReveal | null>(null);

  const form = useForm<{
    name: string;
    email: string;
    depositFeeBps: number;
    disbursementFeeIdr: number;
    apiAccessEnabled: boolean;
    depositWebhookUrl: string;
    enablePayok: boolean;
  }>({
    initialValues: {
      name: '',
      email: '',
      depositFeeBps: 70,
      disbursementFeeIdr: 6500,
      apiAccessEnabled: true,
      depositWebhookUrl: '',
      enablePayok: true,
    },
    validate: {
      name: (v) => (v.trim().length === 0 ? t('merchantCreate.valRequired') : null),
      email: (v) => (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v) ? null : t('merchantCreate.valInvalidEmail')),
      depositFeeBps: (v) => (v < 0 || v > 10000 ? '0–10000' : null),
      disbursementFeeIdr: (v) => (v < 0 || v > 999_999_999 ? '0–999_999_999' : null),
    },
  });

  const mutation = useMutation({
    meta: { successKey: 'notify.merchant.create.ok', errorKey: 'notify.merchant.create.fail' },
    mutationFn: (input: CreateMerchantInput) => merchantsApi.create(input),
    onSuccess: (data) => setRevealed(data),
  });

  if (!profile.isSuperuser && profile.userType !== 'internal') {
    return (
      <Alert color="red" icon={<IconAlertCircle size={16} />}>
        {t('merchantCreate.internalOnly')}
      </Alert>
    );
  }

  const onSubmit = form.onSubmit((v) => {
    const input: CreateMerchantInput = {
      name: v.name.trim(),
      email: v.email.trim(),
      depositFeeBps: v.depositFeeBps,
      disbursementFeeIdr: v.disbursementFeeIdr,
      apiAccessEnabled: v.apiAccessEnabled,
      depositWebhookUrl: v.depositWebhookUrl.trim() || null,
    };
    // PAYOK connection config (base URL, MID, endpoints, keys, notification URLs)
    // is managed centrally on the Acquirer — the backend ignores every per-merchant
    // PAYOK field except `enabled`, so we send only the on/off toggle here.
    if (profile.isSuperuser) {
      input.payok = { enabled: v.enablePayok };
    }
    mutation.mutate(input);
  });

  return (
    <Stack gap="md">
      <Group gap="sm">
        <Button component={Link} to="/dashboard/merchants" variant="default" leftSection={<IconArrowLeft size={14} />}>
          {t('common.back')}
        </Button>
        <Title order={2}>{t('merchantCreate.title')}</Title>
      </Group>

      <form onSubmit={onSubmit}>
        <Stack gap="md">
          <Card>
            <Stack gap="sm">
              <Text fw={600}>{t('merchantCreate.basics')}</Text>
              <Grid>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <TextInput label={t('merchantCreate.nameLabel')} placeholder="Tokopedia" required {...form.getInputProps('name')} />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <TextInput label={t('merchantCreate.emailLabel')} placeholder="ops@toko.com" required {...form.getInputProps('email')} />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <NumberInput
                    label={t('merchantCreate.depositFeeLabel')}
                    description={t('merchantCreate.depositFeeDesc')}
                    min={0}
                    max={10000}
                    {...form.getInputProps('depositFeeBps')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <NumberInput
                    label={t('merchantCreate.disbursementFeeLabel')}
                    min={0}
                    max={999_999_999}
                    thousandSeparator
                    {...form.getInputProps('disbursementFeeIdr')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <Switch
                    label={t('merchantCreate.apiAccessLabel')}
                    mt="lg"
                    {...form.getInputProps('apiAccessEnabled', { type: 'checkbox' })}
                  />
                </Grid.Col>
              </Grid>
            </Stack>
          </Card>

          <Card>
            <Stack gap="sm">
              <Text fw={600}>{t('merchantCreate.webhookTitle')}</Text>
              <TextInput
                label={t('merchantCreate.depositWebhookLabel')}
                placeholder="https://merchant.com/webhooks/transpay"
                description={t('merchantCreate.depositWebhookDesc')}
                disabled={!profile.isSuperuser}
                {...form.getInputProps('depositWebhookUrl')}
              />
            </Stack>
          </Card>

          {profile.isSuperuser && (
            <Card>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text fw={600}>{t('merchantCreate.payokTitle')}</Text>
                  <Switch
                    label={t('merchantCreate.payokEnable')}
                    {...form.getInputProps('enablePayok', { type: 'checkbox' })}
                  />
                </Group>
                <Alert color="blue" icon={<IconAlertCircle size={16} />} variant="light">
                  {t('merchantCreate.payokManagedNote')}
                </Alert>
              </Stack>
            </Card>
          )}

          <Group justify="flex-end">
            <Button variant="default" component={Link} to="/dashboard/merchants">
              {t('merchantCreate.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending} leftSection={<IconPlus size={14} />}>
              {t('merchantCreate.create')}
            </Button>
          </Group>
        </Stack>
      </form>

      <Modal
        opened={revealed !== null}
        onClose={() => {
          setRevealed(null);
          navigate(`/dashboard/merchants/${revealed?.data.id ?? ''}`);
        }}
        title={
          <Group gap="sm">
            <IconCheck color="green" />
            <Text fw={600}>{t('merchantCreate.revealTitle')}</Text>
          </Group>
        }
        size="lg"
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        {revealed && (
          <Stack gap="md">
            <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
              {t('merchantCreate.revealWarning')}
            </Alert>
            <RevealField label={t('merchantCreate.fieldClientId')} value={revealed.credentials.transpayClientId} />
            <RevealField label={t('merchantCreate.fieldClientToken')} value={revealed.credentials.transpayClientToken} />
            {revealed.webhookSecret && (
              <RevealField label={t('merchantCreate.fieldWebhookSecret')} value={revealed.webhookSecret} />
            )}
            <Divider />
            <Text size="sm" c="dimmed">
              {t('merchantCreate.revealFooterPre')}{' '}
              <Code>merchant_api_ip_whitelist</Code> {t('merchantCreate.revealFooterPost')}
            </Text>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}

function RevealField({ label, value }: { label: string; value: string }) {
  const { t } = useTranslation();
  return (
    <Stack gap={4}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Group gap="xs" align="center">
        <Code style={{ flex: 1, fontSize: 12, padding: '6px 10px', wordBreak: 'break-all' }}>
          {value}
        </Code>
        <CopyButton value={value} timeout={1500}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? t('apiCred.copied') : t('apiCred.copy')}>
              <ActionIcon variant={copied ? 'filled' : 'default'} color={copied ? 'green' : 'gray'} onClick={copy}>
                {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      </Group>
    </Stack>
  );
}
